<template>
  <div class="report-print-table">
    <physical-chemical-flex-table-title
      class="mb-1"
      :norm="rule"
      :show-custom-name-below="false"
    />
    <s-table
      small
      bordered
      :striped="false"
      :items="items"
      :fields="fields"
    >
      <template #thead-top>
        <b-tr>
          <b-th>{{ $t('engineering.reports.test') }}</b-th>
          <b-th>{{ $t('engineering.physical-chemical.print.sample-value') }}</b-th>
          <b-th
            class="text-center"
            colspan="2"
          >
            {{ $t('general.diagnostic') }}
          </b-th>
        </b-tr>
      </template>
      <template #cell(text)="data">
        <div class="report-print-table__display-print">
          {{ $t(data.value, data.item.textParams) }}
        </div>
      </template>
      <template #cell(value)="data">
        <div class="report-print-table__display-print">
          {{ getTableValue(data.item.key) }}
        </div>
      </template>
       <template #cell(classification)="data">
        <div class="report-print-table__display-print">
          {{
              tableClassificationReportProxy(rule.rules, data.item.key, rule.norm)
          }}
        </div>
      </template>
      <template #cell(report)="data">
        <div class="report-print-table__display-print"
          :class="{ 'report-print-table__left-align': alignTextToLeft(rule.rules, data.item.key) }"
          >
          {{ tableReportProxy(rule.rules, data.item.key, rule.norm) }}
        </div>
      </template>
    </s-table>
  </div>
</template>

<script>
import { ClassificationType } from 'sigma-mappings';
import PhysicalChemicalFlexTableTitle from '@/domain/components/installation/engineering/physicalChemical/PhysicalChemicalFlexTableTitle';
import GeneralService from '@/domain/services/installation/engineering/physicalChemical/generalTab';

const OK_VALUE = 'OK';

const EMPTY_VALUE = '--';

export default {
  name: 'ReportPrintTable',
  components: { PhysicalChemicalFlexTableTitle },
  props: {
    rule: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
  },
  computed: {
    fields() {
      return [
        {
          key: 'text',
          label: '',
          thClass: 'report-print-table__test',
        },
        {
          key: 'value',
          label: '',
          thClass: 'report-print-table__sample-value',
        },
        {
          key: 'classification',
          label: this.$t('general.classification'),
          thClass: 'report-print-table__classification',
        },
        {
          key: 'report',
          label: this.$t('engineering.physical-chemical.print.report'),
          thClass: 'report-print-table__report',
        },
      ];
    },
  },
  methods: {
    classificationLabel(classification) {
      const classificationTypes = {
        [ClassificationType.ORDINARY]: 'enums.commons.classification-type.ordinary',
        [ClassificationType.URGENT]: 'enums.commons.classification-type.urgent',
        [ClassificationType.CRITICAL]: 'enums.commons.classification-type.critical',
        [ClassificationType.OBSERVATION]: 'enums.commons.classification-type.observation',
      };
      return classificationTypes[classification];
    },
    validateIfObjKeyHasValue(testObject, field, value) {
      const keyValue = testObject[field];
      return keyValue !== undefined && (value === undefined || value == null);
    },
    alignTextToLeft(values, field) {
      return this.tableReportProxy(values, field) !== EMPTY_VALUE
        && this.tableReportProxy(values, field) !== this.$t('engineering.normal');
    },
    tableReportProxy(values, field) {
      const value = values[0][field];
      if (value != null
        && this.getFieldValue(value, field).description === OK_VALUE) {
        return this.$t('engineering.normal');
      }
      if (value != null) {
        const { description } = this.getFieldValue(value, field);
        return description;
      }
      return EMPTY_VALUE;
    },
    tableClassificationReportProxy(values, field) {
      const value = values[0][field];
      if (value != null) {
        const { classification } = this.getFieldValue(value, field);
        return this.$t(this.classificationLabel(classification));
      }
      return EMPTY_VALUE;
    },
    getTableValue(field) {
      const value = this.table.tests[field];
      return this.getFieldValue(value, field);
    },
    getFieldValue(value, field) {
      const valueType = typeof value;
      const processedValue = {
        number: this.$n(value),
        boolean: this.getBooleanFieldsLabel(value, field),
      };
      return processedValue[valueType] || value || '--';
    },
    getBooleanFieldsLabel(value, field) {
      const translationResolver = GeneralService.sampleListRenderingHelpers
        .valueTranslationPathResolvers[field];
      if (translationResolver) {
        return this.$t(translationResolver(value));
      }
      return value;
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  ::v-deep
    .report-print-table
      &__test
        width: 475px
      &__classification
        width: 150px
      &__sample-value
        width: 225px
      &__report
        text-align: center !important
      &__left-align
        text-align: left !important
    tbody tr td:last-child
      text-align: center !important
    .flex-table-title
      font-size: 1.2rem
    .physical-chemical-title
      text-align: left
    th, td
      border: 1px solid $color-gray-intermediary
    thead tr:nth-child(2) th
      background-color: var(--color-background-dark)
  @media print
    .report-print-table
      &__display-print
        float: none
        display: block
</style>
